import { useEffect, useMemo, useState } from "react";
import { Autocomplete, Fade } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectActiveRobotVersion } from "@/store/ses/sesRobotSlice";
import { changeConnectorName, changeNumberValuesMessengerConnector, changeStringValuesMessengerConnector } from "@/store/ses/sesConnectorSlice";
import { selectDataServers } from "@/store/serverSlice";
import { useAccessRight, useTranslate } from "@/hooks";
import { SES } from "@/constants/accessRights";
import { colorPrimary } from "@/constants/colors";
import H3 from "@/ui/H3/H3";
import CustomTextField from "@/components/MUI/CustomTextField/CustomTextField";
import Activator from "../Activator/Activator";
import FieldValueBlock from "../FieldValueBlock/FieldValueBlock";
import { IMessengerProps } from "./Messenger.props";
import styles from "./Messenger.module.scss";
import { Hint } from "@/HOC";

const Messenger = ({ selectId, data: { channel, settings }, connectorName, changeFlgHandler }: IMessengerProps): JSX.Element => {
	const [inputConnectorName, setInputConnectorName] = useState<string>(""); // название коннектора
	const [inputBotName, setInputBotName] = useState<string>(""); // название бота
	const [inputGroup, setInputGroup] = useState<string>(""); // группа
	const [inputResetPhrase, setInputResetPhrase] = useState<string>(""); // фраза сброса сессии
	const [inputUpdateTimer, setInputUpdateTimer] = useState<number>(0); // таймер обновления новых сообщений
	const [inputToken, setInputToken] = useState<string>(""); // токен
	const [inputServer, setInputServer] = useState<string>(""); // обслуживающий сервер

	const dispatch = useAppDispatch();
	const activeRobotVersion = useAppSelector(selectActiveRobotVersion); // store - версия активного робота
	const dataServers = useAppSelector(selectDataServers); // store - данные о серверах

	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	const isDisabled = useMemo(() => {
		return !isAccess(SES.CONNECTOR_MODIFY) || activeRobotVersion === "backup";
	}, [activeRobotVersion]);

	// следим за каналом, названием коннектора и заполняем данные
	useEffect(() => {
		setInputConnectorName(connectorName);
		setInputBotName(settings.botName);
		setInputToken(settings.token);
		setInputServer(settings.server);
		setInputUpdateTimer(settings.pollingInterval);
		channel === "vk" && setInputGroup("groupId" in settings ? settings.groupId : "");
		setInputResetPhrase("reset" in settings && settings.reset ? settings.reset : "");
	}, [selectId, channel, connectorName, settings]);

	// обработчик изменения названия коннектора
	const connectorNameChangeHandler = (): void => {
		if (connectorName === inputConnectorName) return;
		dispatch(changeConnectorName({ newName: inputConnectorName }));
		changeFlgHandler("name");
	};

	// обработчик изменения названия бота
	const botNameChangeHandler = (): void => {
		if (settings.botName === inputBotName) return;
		dispatch(changeStringValuesMessengerConnector({ channel, field: "botName", value: inputBotName }));
		changeFlgHandler("params");
	};

	// обработчик изменения группы
	const groupChangeHandler = (): void => {
		if (channel === "vk" && "groupId" in settings && settings.groupId === inputGroup) return;
		dispatch(changeStringValuesMessengerConnector({ channel, field: "groupId", value: inputGroup }));
		changeFlgHandler("params");
	};

	// обработчик изменения фразы сброса сессии
	const resetPhraseChangeHandler = (): void => {
		if ("reset" in settings && settings.reset && settings.reset === inputResetPhrase) return;
		dispatch(changeStringValuesMessengerConnector({ channel, field: "reset", value: inputResetPhrase }));
		changeFlgHandler("params");
	};

	// обработчик изменения таймера обновления
	const updateTimerChangeHandler = (): void => {
		if (settings.pollingInterval === inputUpdateTimer) return;
		dispatch(changeNumberValuesMessengerConnector({ channel, field: "pollingInterval", value: inputUpdateTimer }));
		changeFlgHandler("params");
	};

	// обработчик изменения токена
	const tokenChangeHandler = (): void => {
		if (settings.token === inputToken) return;
		dispatch(changeStringValuesMessengerConnector({ channel, field: "token", value: inputToken }));
		changeFlgHandler("params");
	};

	// обработчик изменения сервера
	const serverChangeHandler = (): void => {
		if (settings.server === inputServer) return;
		dispatch(changeStringValuesMessengerConnector({ channel, field: "server", value: inputServer }));
		changeFlgHandler("params");
	};

	return (
		<Fade in={true} timeout={500}>
			<div className={styles.container}>
				{/* общие настройки */}
				<H3 className={styles.header}>{translate("title_generalSettings")}</H3>
				<div className={styles.containerInner}>
					<div className={styles.block}>
						{/* название коннектора */}
						<Hint hint={"hint_connectorsTitle"} width="100%" position="left">
							<CustomTextField
								required
								label={translate("input_connectorName")}
								value={inputConnectorName}
								onChange={(e) => setInputConnectorName(e.target.value)}
								onBlur={connectorNameChangeHandler}
								error={!inputConnectorName}
								disabled={isDisabled}
							/>
						</Hint>

						{/* группа */}
						{channel === "vk" && (
							<Hint hint={"hint_connectorsVkGroup"} width="100%">
								<CustomTextField
									required
									label={translate("input_group")}
									value={inputGroup}
									onChange={(e) => setInputGroup(e.target.value)}
									onBlur={groupChangeHandler}
									error={!inputGroup}
									disabled={isDisabled}
								/>
							</Hint>
						)}

						{/* фраза сброса сессии */}
						<Hint hint={"hint_connectorsResetPhrase"} width="100%">
							<CustomTextField
								label={translate("input_sessionResetPhrase")}
								fullWidth
								value={inputResetPhrase}
								onChange={(e) => setInputResetPhrase(e.target.value)}
								onBlur={resetPhraseChangeHandler}
								disabled={isDisabled}
							/>
						</Hint>
					</div>

					{/* токен */}
					<div className={styles.block}>
						<Hint hint={"hint_connectorsToken"} width="100%">
							<CustomTextField
								required
								label={translate("input_token")}
								value={inputToken}
								onChange={(e) => setInputToken(e.target.value)}
								onBlur={tokenChangeHandler}
								error={!inputToken}
								disabled={isDisabled}
							/>
						</Hint>
					</div>

					<div className={styles.block}>
						{/* название бота */}
						<Hint hint={"hint_connectorsBotName"} width="100%">
							<CustomTextField
								required
								label={translate("input_botName")}
								value={inputBotName}
								fullWidth
								onChange={(e) => setInputBotName(e.target.value)}
								onBlur={botNameChangeHandler}
								error={!inputBotName}
								disabled={isDisabled}
							/>
						</Hint>
						{/* сервер */}
						<Hint hint={"hint_connectorsServingServer"} width="100%">
							<Autocomplete
								fullWidth
								freeSolo
								options={Object.values(dataServers?.names || {})}
								value={inputServer}
								onChange={(_, value) => setInputServer(value ? value : "")}
								onBlur={serverChangeHandler}
								disabled={isDisabled}
								renderInput={(params) => (
									<CustomTextField {...params} required label={translate("input_servingServer")} onChange={(e) => setInputServer(e.target.value)} error={!inputServer} />
								)}
								renderOption={(props, option) => {
									return (
										<span {...props} style={{ fontSize: 13, color: colorPrimary }} key={option}>
											{option}
										</span>
									);
								}}
							/>
						</Hint>
						{/* таймер обновления */}
						<Hint hint={"hint_connectorsUpdateFrequency"} width="100%">
							<CustomTextField
								required
								type="number"
								label={translate("input_updateFrequency")}
								value={inputUpdateTimer}
								onChange={(e) => setInputUpdateTimer(+e.target.value)}
								onBlur={updateTimerChangeHandler}
								error={inputUpdateTimer < 1}
								disabled={isDisabled}
								InputProps={{
									inputProps: { min: 1 },
								}}
							/>
						</Hint>
					</div>
				</div>

				{/* фразы-активаторы */}
				<H3 className={styles.header}>{translate("title_activatorPhrases")}</H3>
				<div className={styles.containerInner}>
					{settings.activators.concat("").map((phrase, idx, array) => (
						<div className={styles.containerInnerField} key={phrase + idx}>
							<Hint hint={"hint_connectorsActivatorPhrases"} width="100%">
								<Activator channel={channel} phrase={phrase} idx={idx} array={array} changeFlgHandler={changeFlgHandler} isDisabled={isDisabled} />
							</Hint>
						</div>
					))}
				</div>

				{/* переменные */}
				<H3 className={styles.header}>{translate("title_variables")}</H3>
				{Object.entries(settings.variables)
					.concat([["", ""]])
					.map(([variable, value], idx, array) => (
						<Hint hint={"hint_connectorsVariable"} width="100%" key={idx}>
							<FieldValueBlock
								channel={channel}
								setting="variables"
								field={variable}
								fieldLabel="input_variable"
								value={value}
								valueLabel="input_value"
								idx={idx}
								array={array}
								changeFlgHandler={changeFlgHandler}
								isDisabled={isDisabled}
							/>
						</Hint>
					))}

				{/* доступы */}
				<H3 className={styles.header}>{translate("title_accesses")}</H3>
				{Object.entries(settings.whitelist)
					.concat([["", ""]])
					.map(([id, desc], idx, array) => (
						<Hint hint={"hint_connectorsUserId"} width="100%" key={idx}>
							<FieldValueBlock
								key={idx}
								channel={channel}
								setting="whitelist"
								field={id}
								fieldLabel="input_userId"
								value={desc}
								valueLabel="input_comment"
								idx={idx}
								array={array}
								changeFlgHandler={changeFlgHandler}
								isDisabled={isDisabled}
							/>
						</Hint>
					))}
			</div>
		</Fade>
	);
};

export default Messenger;
